import React, { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  Slide,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { Close, Search, Upload, AutoAwesome, Rocket, Palette } from '@mui/icons-material'
import { useCreateTopic } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { TopicType } from '@/utils/constants'
import { isBabylonType } from '@/utils/helper'

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />
})

interface CreationOption {
  id: string
  title: string
  description: string
  icon: any
  category: string
  gradient: string
  preview?: string
  isNew?: boolean
  isPro?: boolean
}

const creationOptions: CreationOption[] = [
  {
    id: 'pattern',
    title: 'Pattern Design',
    description: 'Create stunning visual patterns and textures for quilts, wallpapers, and more',
    icon: SVGS.FilterTopicAll,
    category: 'Creative',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    preview: '/api/placeholder/200/120',
  },

  {
    id: 'gallery',
    title: 'Gallery Design',
    description: 'Create stunning visual patterns and textures for quilts, wallpapers, and more',
    icon: SVGS.FilterTopicAll,
    category: 'Creative',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    preview: '/api/placeholder/200/120',
  },

  {
    id: 'album',
    title: 'Photo Album',
    description: 'Curate and organize your photo albums with beautiful layouts and more',
    icon: SVGS.FilterTopicAlbum,
    category: 'Creative',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    preview: '/api/placeholder/200/120',
  },
]

const quickActions = [
  {
    id: 'upload',
    title: 'Upload Images',
    description: 'Import photos for your albums',
    icon: Upload,
    action: 'upload',
  },
  {
    id: 'explore-designs',
    title: 'Explore Designs',
    description: 'Discover inspiring patterns and templates',
    icon: Palette,
    action: 'templates',
  },
]

interface ModernCreateModalProps {
  open: boolean
  onClose: () => void
  onCreateProject?: (type: string, title: string) => void
}

export const ModernCreateModal: React.FC<ModernCreateModalProps> = ({
  open,
  onClose,
  onCreateProject,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [projectTitle, setProjectTitle] = useState('')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [ewUser] = useLocalStorage('ewUser', null)
  const createTopic = useCreateTopic(ewUser?.userID)
  const navigate = useNavigate()

  const categories = ['All', 'Creative']

  const filteredOptions = creationOptions.filter((option) => {
    const matchesSearch =
      option.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || option.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleCreate = async () => {
    if (!selectedType || !projectTitle.trim()) return

    setIsSubmitting(true)
    try {
      // Map the selectedType to TopicType
      const topicType =
        selectedType === 'pattern'
          ? TopicType.PATTERN
          : selectedType === 'gallery'
            ? TopicType.GALLERY
            : TopicType.ALBUM

      const result = await createTopic.mutateAsync({
        topicName: projectTitle.trim(),
        topicType: topicType.toString(),
        allowContribution: 0,
        topicXData: 'X Data Here',
        privacyType: 3,
        cliques: [],
        connections: [],
      })

      if (result.isError) {
        toast.error('Create failed. Please try again!')
      } else {
        toast.success(`${selectedType === 'pattern' ? 'Pattern' : 'Album'} created successfully!`)

        // Navigate based on type
        if (isBabylonType(topicType)) {
          navigate(`/pattern?topicID=${result.topicID}&mode=edit`)
        } else {
          navigate(`/preview?topicID=${result.topicID}&topicType=${topicType}`)
        }

        // Reset and close
        setProjectTitle('')
        setSelectedType(null)
        onClose()

        // Call the optional callback
        if (onCreateProject) {
          onCreateProject(selectedType, projectTitle.trim())
        }
      }
    } catch (e) {
      toast.error('Create failed. Please try again!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuickAction = (action: string) => {
    if (action === 'upload') {
      onClose()
      navigate('/workspace/album')
    } else if (action === 'templates') {
      onClose()
      navigate('/discover/pattern')
    }
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={onClose}
      maxWidth='xl'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          backgroundColor: '#fafafa',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Box>
          <Typography variant='h4' fontWeight={700} color='#1a1a1a'>
            Get started
          </Typography>
          <Typography variant='body2' color='#666666' mt={0.5}>
            Create beautiful patterns or organize your photo collections
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search */}
          <TextField
            placeholder='Try "pattern" or "album"'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size='small'
            sx={{
              width: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: '24px',
                backgroundColor: '#f5f5f5',
                border: 'none',
                '& fieldset': { border: 'none' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search sx={{ color: '#666666', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Upload Button */}
          <Button
            variant='contained'
            startIcon={<Upload />}
            sx={{
              backgroundColor: '#1a1a1a',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
            onClick={() => handleQuickAction('upload')}
          >
            Upload media
          </Button>

          {/* Close Button */}
          <IconButton onClick={onClose} sx={{ color: '#666666' }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', height: 'calc(90vh - 120px)' }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: 280,
            backgroundColor: '#ffffff',
            borderRight: '1px solid #e0e0e0',
            p: 3,
            overflowY: 'auto',
          }}
        >
          {/* Categories */}
          <Typography variant='h6' fontWeight={600} mb={2} color='#1a1a1a'>
            Categories
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: '8px',
                  height: 40,
                  fontSize: '14px',
                  fontWeight: selectedCategory === category ? 600 : 400,
                  backgroundColor: selectedCategory === category ? '#1a1a1a' : 'transparent',
                  color: selectedCategory === category ? '#ffffff' : '#666666',
                  border: selectedCategory === category ? 'none' : '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: selectedCategory === category ? '#333333' : '#f5f5f5',
                  },
                }}
              />
            ))}
          </Box>

          {/* Quick Actions */}
          <Typography variant='h6' fontWeight={600} mb={2} color='#1a1a1a'>
            Quick actions
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {quickActions.map((action) => (
              <Button
                key={action.id}
                startIcon={<action.icon />}
                onClick={() => handleQuickAction(action.action)}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  borderRadius: '8px',
                  p: 2,
                  textTransform: 'none',
                  color: '#666666',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <Box>
                  <Typography variant='body2' fontWeight={500} color='#1a1a1a'>
                    {action.title}
                  </Typography>
                  <Typography variant='caption' color='#666666'>
                    {action.description}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
          {/* Creation Options Grid */}
          <Grid container spacing={3}>
            {filteredOptions.map((option) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={option.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '12px',
                    border: selectedType === option.id ? '2px solid #1a1a1a' : '1px solid #e0e0e0',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                  }}
                  onClick={() => setSelectedType(option.id)}
                >
                  {/* Preview Image */}
                  <Box
                    sx={{
                      height: 120,
                      background: option.gradient,
                      borderRadius: '12px 12px 0 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {/* Badges */}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                      {option.isNew && (
                        <Chip
                          label='NEW'
                          size='small'
                          sx={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            fontSize: '10px',
                            height: 20,
                          }}
                        />
                      )}
                      {option.isPro && (
                        <Chip
                          label='PRO'
                          size='small'
                          sx={{
                            backgroundColor: '#FF9800',
                            color: 'white',
                            fontSize: '10px',
                            height: 20,
                          }}
                        />
                      )}
                    </Box>

                    {/* Icon */}
                    <Box
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        p: 2,
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {typeof option.icon === 'string' ? (
                        <SVG src={option.icon} width={32} height={32} style={{ color: 'white' }} />
                      ) : (
                        <option.icon sx={{ fontSize: 32, color: 'white' }} />
                      )}
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='h6' fontWeight={600} color='#1a1a1a' mb={0.5}>
                      {option.title}
                    </Typography>
                    <Typography variant='body2' color='#666666' fontSize='13px'>
                      {option.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {filteredOptions.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
                color: '#666666',
              }}
            >
              <Search sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant='h6' fontWeight={500} mb={1}>
                No results found
              </Typography>
              <Typography variant='body2'>
                Try adjusting your search or browse different categories
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      {selectedType && (
        <Box
          sx={{
            p: 3,
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e0e0e0',
            borderRadius: '0 0 16px 16px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, maxWidth: 600 }}>
            <TextField
              placeholder={`Enter ${filteredOptions
                .find((o) => o.id === selectedType)
                ?.title.toLowerCase()} name`}
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              fullWidth
              size='medium'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />

            <Button
              variant='contained'
              onClick={handleCreate}
              disabled={!projectTitle.trim() || isSubmitting}
              sx={{
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                minWidth: 120,
                '&:hover': {
                  backgroundColor: '#333333',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999999',
                },
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  Creating...
                </Box>
              ) : (
                'Create'
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  )
}

export default ModernCreateModal
